import store from '@/store';

import {
  IRouterMiddleware,
  MiddlewareRoute,
  MiddlewareCallback
} from '../middleware.interface';
import { OnboardingURL, getSignUpUrlRoutes } from '@/views/Onboarding/helpers';
import SignUpService from '@/api/SingupService';
import { app } from '@/main';
import { IBusinessGoogleLocationData, IBusinessType } from '@/models/business';
import { removeCountryCode } from '@/utils/string';
import { loadCategoriesStore } from '@/utils/categories';
import { createBusinessPayload } from '@/utils/createBusinessPayload';
import { omitNilProperties } from '@/utils/omitNilProperties';
import APIService, { setAppCookie } from '@/api';
import segmentIo from '@/utils/segment.io';
import flagKeys from '@/constants/flagKeys';

const signUpService = new SignUpService();

class AISignupRedirect {
  constructor(
    private readonly to: MiddlewareRoute,
    private readonly from: MiddlewareRoute,
    private readonly next: MiddlewareCallback
  ) {}

  get flags() {
    return store.getters['flags/getFlags'] || [];
  }

  get businessOnboardingState() {
    return store.getters['businessOnboarding/getBusinessOnboardingState'];
  }

  async saveBusinessInfo(data: IBusinessGoogleLocationData, placeId: string) {
    const parsedWebsite = data.websiteUri
      ? new URL(data.websiteUri).hostname
      : '';

    app.$store.dispatch('businessOnboarding/SET_STORE_DETAILS_FORM', {
      address: data.location,
      aditionalAddress: '',
      businessName: data.name,
      locationName: data.location.address,
      subCategories: data.subcategories,
      website: parsedWebsite,
      phone: {
        code: '1',
        iso: 'US',
        number: data.internationalPhoneNumber
          ? removeCountryCode(data.internationalPhoneNumber).replace(
              /[^0-9]+/g,
              ''
            )
          : ''
      },
      placeId
    });

    app.$store.dispatch('businessOnboarding/SET_SELECTED_COUNTRY', {
      name: 'United States',
      phoneMask: '(XXX) XXX-XXXX'
    });

    app.$store.dispatch(
      'businessOnboarding/SET_OPERATION_TYPE',
      parsedWebsite ? IBusinessType.BOTH : IBusinessType.INSTORE
    );

    const category = data.category;

    await loadCategoriesStore();

    const categories =
      app.$store.getters['businessOnboarding/getCategories'] || [];

    if (categories.find((item: any) => item.label === category.name))
      app.$store.dispatch('businessOnboarding/SET_SELECTED_CATEGORY', {
        ...category,
        otherValue: category.name.toLowerCase().includes('other')
          ? category.name
          : ''
      });
    else {
      const newCategory = categories.find((item: any) =>
        item.label.toLowerCase().includes('other')
      );
      app.$store.dispatch('businessOnboarding/SET_SELECTED_CATEGORY', {
        name: newCategory.label,
        id: newCategory.value,
        otherValue: newCategory.label
      });
    }
  }

  async handlePreValidateBusiness() {
    const businessData = createBusinessPayload(this.businessOnboardingState);

    const payload = {
      business: {
        ...omitNilProperties(businessData),
        countryIso2: 'US'
      }
    };

    try {
      const data = (await signUpService.preValidate(payload)) as any;

      if (data.status === 200) {
        app.$store.dispatch('businessOnboarding/SET_PREFILLED_BUSINESS_ID', '');

        return true;
      }

      const { response } = data;

      const errorData = response.data;

      if (
        errorData.slug &&
        errorData.slug === 'exceptions.business.theSameAddress'
      ) {
        app.$store.dispatch(
          'businessOnboarding/SET_PREFILLED_BUSINESS_ID',
          errorData.data.businessId
        );
        return false;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  handleRedirect() {
    (async () => {
      const placeId = this.to.params.placeId;

      if (this.flags.includes(flagKeys.physicalBusinessOnboardingQ12026)) {
        setAppCookie('instore');

        window.location.reload();
        return;
      }

      signUpService.disableInterception();
      if (APIService.token) {
        this.next('/');
        return;
      }
      if (segmentIo.getIdentifiedId()) {
        segmentIo.reset();
      }
      segmentIo.track('AI Sign Up | Flow Initiation', {
        'Place ID': placeId
      });
      try {
        const { data } = await signUpService.getGoogleAdressesDetail(placeId);

        await this.saveBusinessInfo(data, placeId);

        const valid = await this.handlePreValidateBusiness();

        const route = getSignUpUrlRoutes() as typeof OnboardingURL;

        this.next({
          path: valid ? route.AI_BUSINESS_PREVIEW : route.AI_BUSINESS_COMPLETE
        });
      } catch (err) {
        segmentIo.track('AI Sign Up | Flow Initiation Error', {
          'Place ID': placeId,
          Error: err
        });
        this.next('/');
      }
    })();
  }

  async execute() {
    this.handleRedirect();
  }
}
export class HandleAISignupRedirect implements IRouterMiddleware {
  canActivate(to: MiddlewareRoute): boolean {
    return ['ai-signup'].includes(to.name!) && !!to.params.placeId;
  }

  async execute(
    to: MiddlewareRoute,
    from: MiddlewareRoute,
    next: MiddlewareCallback
  ): Promise<void> {
    const instance = new AISignupRedirect(to, from, next);
    instance.execute();
  }
}
